/**
 * Contract Deal Report Model
 */
import { t } from 'i18next'

import type { ListOrganizationDealReportsResponse_DealReport } from '@enechain/eposition-proto/bffportal/v1/report_service_pb'
import {
  DealPosition,
  DeliveryType,
} from '@enechain/eposition-proto/bfftypes/v1/deal_pb'
import { convertDeliveryHourTypeLabelLong } from '@enechain/eposition-ui'
import { convertAreaLabelFromAreaCode } from '@enechain/eposition-ui/src/models/area'
import { formatGoogleDate } from '@enechain/eposition-ui/src/models/date'

import { ContractListFilterState } from '~/pages/ContractList/hooks/useContractListFilter'
import { ContractCsvRow } from '~/pages/ContractList/types/types'

export type ContractDealReportModel = {
  organizationId: string
  dealtDate: string
  platformName: string
  eclearDealCode: string
  dealPosition: DealPosition
  areaCode: string
  // 差額精算対象エリア。クロスエリア取引でのみ埋まり、通常は空文字
  settlementDifferenceTargetArea: string
  deliveryStartDateString: string
  deliveryEndDateString: string
  deliveryStartDate: Date
  deliveryEndDate: Date
  deliveryType: string
  counterDeliveryType: string
  deliveryTypeString: string
  deliveryHourType: string
  volumeInKw: string
  totalDeliveryHour: string
  totalKwh: string
  unitPricePerKwh: string
  floatingIndexFormula: string
  currentMarketPrice: string
  mtm: string
  clearingFeeRate: string
  fee: string
  isSettled: boolean
  isPositiveMtm: boolean
}

export function convertContractDealReportModel(
  data: ListOrganizationDealReportsResponse_DealReport,
): ContractDealReportModel {
  const volumeInKw =
    data.volumeInKw?.value != null && data.volumeInKw.value !== ''
      ? Number(data.volumeInKw.value)
      : undefined
  const totalKwh =
    data.totalKwh?.value != null && data.totalKwh.value !== ''
      ? Number(data.totalKwh.value)
      : undefined
  const clearingFeeRate =
    data.clearingFeeRate?.value != null && data.clearingFeeRate.value !== ''
      ? Number(data.clearingFeeRate.value)
      : undefined
  const fee =
    data.fee?.value != null && data.fee.value !== ''
      ? Number(data.fee.value)
      : undefined

  const ownSideDeliveryType = convertDeliveryTypeString(data.deliveryType)
  const counterSideDeliveryType = convertDeliveryTypeString(
    data.counterDeliveryType,
  )

  const deliveryTypeString = t('page:contract_list.delivery_type_format', {
    ownSide: ownSideDeliveryType,
    counterSide: counterSideDeliveryType,
  })
  if (
    data.deliveryStartDate === undefined ||
    data.deliveryEndDate === undefined
  ) {
    throw new Error('deliveryStartDate or deliveryEndDate is undefined')
  }

  return {
    organizationId: data.organizationId,
    dealtDate:
      data.dealtDate !== undefined
        ? formatGoogleDate(data.dealtDate, '/')
        : t('domain:model.posting_status.provisional'),
    platformName: data.platformName,
    eclearDealCode: data.eclearDealCode,
    dealPosition: data.dealPosition,
    areaCode: convertAreaLabelFromAreaCode(data.areaCode),
    settlementDifferenceTargetArea:
      data.settlementDifferenceTargetAreaCode !== undefined
        ? convertAreaLabelFromAreaCode(data.settlementDifferenceTargetAreaCode)
        : '',
    deliveryHourType: convertDeliveryHourTypeLabelLong(data.deliveryHourType),
    deliveryStartDateString:
      data.deliveryStartDate !== undefined
        ? formatGoogleDate(data.deliveryStartDate, '/')
        : '',
    deliveryEndDateString:
      data.deliveryEndDate !== undefined
        ? formatGoogleDate(data.deliveryEndDate, '/')
        : '',
    deliveryStartDate: new Date(
      data.deliveryStartDate.year,
      data.deliveryStartDate.month - 1,
      data.deliveryStartDate.day,
    ),
    deliveryEndDate: new Date(
      data.deliveryEndDate.year,
      data.deliveryEndDate.month - 1,
      data.deliveryEndDate.day,
    ),
    deliveryType: ownSideDeliveryType,
    counterDeliveryType: counterSideDeliveryType,
    deliveryTypeString,
    volumeInKw: volumeInKw !== undefined ? volumeInKw.toLocaleString() : '',
    totalDeliveryHour: data.totalDeliveryHour.toLocaleString(),
    totalKwh: totalKwh !== undefined ? totalKwh.toLocaleString() : '',
    unitPricePerKwh: data.unitPricePerKwh,
    floatingIndexFormula: data.formula ?? '',
    currentMarketPrice: data.currentMarketPrice ?? '-',
    mtm: data.mtm.toLocaleString(),
    clearingFeeRate:
      clearingFeeRate !== undefined ? clearingFeeRate.toLocaleString() : '',
    fee: fee !== undefined ? fee.toLocaleString() : '',
    isSettled: data.isSettled,
    isPositiveMtm: data.mtm >= 0,
  }
}

function convertDeliveryTypeString(deliveryType: DeliveryType): string {
  switch (deliveryType) {
    case DeliveryType.BG:
      return t('domain:shared.delivery_type.bg_delivery')
    case DeliveryType.JEPX:
      return t('domain:shared.delivery_type.jepx_delivery')
    case DeliveryType.EEX:
      return t('domain:shared.delivery_type.eex')
  }
  return ''
}

export function convertContractDealReportModels(
  data: ListOrganizationDealReportsResponse_DealReport[],
): ContractDealReportModel[] {
  return data.map(convertContractDealReportModel)
}

export function convertToContractCsvRow(
  model: ContractDealReportModel,
): ContractCsvRow {
  return {
    dealtDate: model.dealtDate,
    platformName: model.platformName,
    eclearDealCode: model.eclearDealCode,
    dealPosition:
      model.dealPosition === DealPosition.BID
        ? t('domain:shared.position.bid_short')
        : t('domain:shared.position.offer_short'),
    areaCode: model.areaCode,
    settlementDifferenceTargetArea: model.settlementDifferenceTargetArea,
    deliveryStartDate: model.deliveryStartDateString,
    deliveryEndDate: model.deliveryEndDateString,
    deliveryTypeString: model.deliveryTypeString,
    deliveryHourType: model.deliveryHourType,
    volumeInKw: model.volumeInKw.replaceAll(',', ''),
    totalDeliveryHour: model.totalDeliveryHour.replaceAll(',', ''),
    totalKwh: model.totalKwh.replaceAll(',', ''),
    unitPricePerKwh: model.unitPricePerKwh.replaceAll(',', ''),
    floatingIndexFormula: model.floatingIndexFormula,
    currentMarketPrice: model.currentMarketPrice.replaceAll(',', ''),
    mtm: model.mtm.replaceAll(',', ''),
    clearingFeeRate: model.clearingFeeRate,
    fee: model.fee.replaceAll(',', ''),
  }
}

type GenerateExportPeriodResult = {
  fromDate: Date
  toDate: Date
}

export function getExportPeriod(
  data: ContractDealReportModel[],
  filterState: ContractListFilterState,
): GenerateExportPeriodResult {
  if (filterState.fromDate !== undefined && filterState.toDate !== undefined) {
    return {
      fromDate: filterState.fromDate,
      toDate: filterState.toDate,
    }
  }
  let fromDate = data[0].deliveryStartDate
  let toDate = data[0].deliveryEndDate
  for (const item of data) {
    if (item.deliveryStartDate < fromDate) {
      fromDate = item.deliveryStartDate
    }
    if (item.deliveryEndDate > toDate) {
      toDate = item.deliveryEndDate
    }
  }
  return {
    fromDate:
      filterState.fromDate !== undefined ? filterState.fromDate : fromDate,
    toDate: filterState.toDate !== undefined ? filterState.toDate : toDate,
  }
}
